/**
 * ScrollClass — LiveView hook that toggles a CSS class on a target element
 * when the hooked element is scrolled away from the top.
 *
 * Multiple scrollable elements can target the same element. A simple voting
 * mechanism (stored in `data-votes-for-scroll-class` on the target) ensures
 * the class is only removed once *every* voter has scrolled back to the top.
 *
 * ## Data attributes
 *
 * | Attribute                    | Description                                       |
 * |------------------------------|---------------------------------------------------|
 * | `data-scroll-class`          | CSS class to add/remove on the target element.    |
 * | `data-scroll-class-target`   | CSS selector for the element that receives the class. |
 *
 * ## Example
 *
 * ```heex
 * <div id="my-scroller" phx-hook="ScrollClass"
 *      data-scroll-class="shadow-inner"
 *      data-scroll-class-target="#header">
 *   …scrollable content…
 * </div>
 * ```
 */
export default {
  ScrollClass: {
    mounted() {
      const scrollClass = this.el.dataset.scrollClass;
      const targetSelector = this.el.dataset.scrollClassTarget;

      this.el.addEventListener('scroll', e => {
        const targetEl = document.querySelector(targetSelector);
        if (!targetEl) return;

        // Rebuild the voter list, excluding this element so we can re-evaluate.
        const votesForScrollClassString = targetEl.dataset.votesForScrollClass || '';
        let votesForScrollClass = votesForScrollClassString
          .split(',')
          .filter(v => v)
          .filter(v => v !== this.el.id);

        if (this.el.scrollTop > 0) {
          votesForScrollClass.push(this.el.id);
          targetEl.classList.add(scrollClass);
        } else {
          if (votesForScrollClass.length === 0) {
            targetEl.classList.remove(scrollClass);
          }
        }

        targetEl.dataset.votesForScrollClass = votesForScrollClass;
      });
    },
  },
};
